'use client';

import { motion } from 'motion/react';
import { useLayoutEffect, useState } from 'react';

import { REVEAL_ROOT_ATTRS } from '@/constants/heroScene';
import { useDiscernibleInView } from '@/hooks/useDiscernibleInView';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { springBadge } from '@/motion/tokens';

import styles from './StatCircleReveal.module.css';

/** Subtle scale-in start — matches docs/04-motion.md (spring-badge). */
const BADGE_SCALE_FROM = 0.85;

const REVEAL_ROOT_SELECTOR = REVEAL_ROOT_ATTRS.map((attr) => `[${attr}]`).join(', ');

type StatCircleRevealProps = {
  children: React.ReactNode;
  className?: string;
  /**
   * When false, render children statically — reserved if a parent timeline
   * already owns the badge appear.
   */
  animateOnView?: boolean;
  /**
   * Seconds to wait after becoming discernible before the spring-badge
   * starts — stagger sibling bubbles (e.g. RLI flanking pods).
   */
  delay?: number;
};

/**
 * Subtle scale-in for `<StatCircle>` once discernibly in view (spring-badge).
 * Waits out GSAP body opacity on reveal roots so the spring is visible when
 * the parent fades in — same contract as `LocationPin`. Under a GSAP reveal
 * root, only scale animates (parent owns fade). Otherwise fade + scale.
 *
 * Section-header asides (`[data-section-aside-reveal]`) and prose-block asides
 * own fade + scale in GSAP — pass `animateOnView={false}` so this leaf does
 * not run a second size-up after the fade.
 * Reduced motion: static.
 */
export function StatCircleReveal({
  children,
  className,
  animateOnView = true,
  delay = 0,
}: StatCircleRevealProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const skipMotion = prefersReducedMotion || !animateOnView;
  const { ref, inView } = useDiscernibleInView<HTMLDivElement>({
    once: true,
    amount: 0.35,
    disabled: skipMotion,
  });
  const [settled, setSettled] = useState(false);
  // Default true so GSAP-wrapped badges never opacity-flash before we detect the root.
  const [underRevealRoot, setUnderRevealRoot] = useState(true);
  const showStatic = skipMotion || settled;
  const transition =
    delay > 0 ? { ...springBadge, delay } : springBadge;

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    setUnderRevealRoot(node.closest(REVEAL_ROOT_SELECTOR) != null);
  }, [ref]);

  return (
    <div
      ref={ref}
      className={[styles.root, className].filter(Boolean).join(' ')}
    >
      {showStatic ? (
        children
      ) : (
        <motion.div
          className={styles.bounce}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          variants={
            underRevealRoot
              ? {
                  hidden: { scale: BADGE_SCALE_FROM },
                  visible: { scale: 1 },
                }
              : {
                  hidden: { scale: BADGE_SCALE_FROM, opacity: 0 },
                  visible: { scale: 1, opacity: 1 },
                }
          }
          transition={transition}
          onAnimationComplete={(definition) => {
            if (definition === 'visible') {
              setSettled(true);
            }
          }}
        >
          {children}
        </motion.div>
      )}
    </div>
  );
}
